import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Search, Loader2, ArrowUpRight, AlertTriangle, CalendarRange, History, CornerDownLeft, BookOpen } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip } from 'recharts';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { THead, Th } from '@/components/ui/table-kit';
import { GStatTile, BarCell } from '@/components/growth/growth-ui';
import { GChartCanvas, useChartTheme } from '@/components/growth/chart-canvas';
import { api } from '@/lib/api';
import { useQbee } from '@/components/qbee/qbee-context';
import { cn, fmtZoned } from '@/lib/utils';

/* ASK COOKIEBEES.
 *
 * The question box at the top of every page, answered in a side panel. The
 * server (api/src/routes/ask.js) has the model read the same modules the
 * reports read and answer in a spec whose every figure is a reference into
 * what those modules returned; a figure the model typed itself is rejected
 * before it gets here. So everything drawn below is data from the server:
 * this file lays it out and never computes a number of its own, apart from
 * choosing the colour of a change.
 *
 * The panel is a drawer, not a dialog: the page stays where it was, and a
 * report link opens that report with the same dates.
 */

const EXAMPLES = [
  'How did revenue this month compare with last month?',
  'Which campaign brought repeat buyers last month?',
  'Which channel brought the most revenue in the last 30 days?',
  'Which channel brought the most qualified leads in the last 30 days?',
];
const TOOL_WORDS = {
  store_summary: 'Store totals', campaigns: 'Campaigns', daily_trend: 'Day by day', lead_channels: 'Leads by channel',
  sales_by_channel: 'Sales by channel', new_vs_returning: 'New and returning', top_products: 'Top products',
  abandoned_checkouts: 'Abandoned checkouts', signal_health: 'Signal health', email_performance: 'Email', whatsapp_broadcasts: 'WhatsApp broadcasts',
  conversions_sent: 'Conversions sent', journey_overlap: 'Orders by journey', search_help: 'The Cookiebees guides', read_help: 'The Cookiebees guides',
};

// A rise is good news for most figures; for these it is not.
const DOWN_IS_GOOD = /refund|abandon|cost|cpl|cpa|ncac|unsub|failed|weak|spend/i;
function chipFor(tile) {
  const ch = tile.compare?.change;
  if (!ch || ch.value == null) return {};
  const v = ch.value;
  const txt = ch.kind === 'pts' ? `${v > 0 ? '+' : ''}${v} pts` : `${v > 0 ? '+' : ''}${v}%`;
  if (v === 0) return { chip: txt, chipTone: 'calm' };
  const up = v > 0;
  const good = DOWN_IS_GOOD.test(`${tile.label} ${tile.ref || ''}`) ? !up : up;
  return { chip: txt, chipTone: good ? 'good' : 'bad' };
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayShort = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '')); return m ? `${Number(m[3])} ${MON[Number(m[2]) - 1]}` : String(s || ''); };
const whenAsked = (s) => { try { return fmtZoned(s, 'd MMM, hh:mm aaa'); } catch { return ''; } };

/* ── the pieces of an answer ─────────────────────────────────────────────── */

const CHIP_TONE = { good: 'text-emerald-700 dark:text-emerald-400', bad: 'text-rose-700 dark:text-rose-400', calm: 'text-muted-foreground' };

function AnswerTiles({ tiles, compact = false }) {
  if (!tiles?.length) return null;
  // In the Qbee dock (about 400px) three tiles side by side cut every label
  // short ("Orders with a Met..."), so the figures stand as a list there:
  // the label in full on the left, the figure on the right.
  if (compact) {
    return (
      <div className="divide-y divide-border rounded-xl border border-[hsl(var(--card-border))] bg-card">
        {tiles.map((t) => {
          const c = chipFor(t);
          return (
            <div key={`${t.label}-${t.ref}`} className="flex items-baseline justify-between gap-3 px-3.5 py-2.5">
              <span className="min-w-0 text-[13px] leading-snug text-muted-foreground">{t.label}</span>
              <span className="shrink-0 text-right">
                <span className="text-[17px] font-semibold tabular-nums text-foreground">{t.display}</span>
                {(c.chip || t.compare) && (
                  <span className="block text-[12px] text-muted-foreground">
                    {c.chip && <span className={cn('font-medium', CHIP_TONE[c.chipTone])}>{c.chip}</span>}
                    {c.chip && t.compare ? ' from ' : ''}{t.compare ? t.compare.display : ''}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className={cn('grid gap-3', tiles.length === 1 ? 'grid-cols-1' : tiles.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2')}>
      {tiles.map((t) => (
        <GStatTile key={`${t.label}-${t.ref}`} label={t.label} value={t.display}
          {...chipFor(t)}
          caption={t.compare ? `${t.compare.display}${t.compare.period ? ` in ${t.compare.period}` : ' the period before'}` : null} />
      ))}
    </div>
  );
}

function AnswerTable({ table, compact = false }) {
  if (!table?.rows?.length) return null;
  const shown = table.rows.length;
  // The dock is narrow: labels wrap instead of cutting short, cells sit
  // closer, and the figure stands without its bar so every column fits.
  const pad = compact ? 'px-2.5 py-2' : 'px-4 py-2';
  return (
    <div className="overflow-hidden rounded-xl border border-[hsl(var(--card-border))] bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <THead className="bg-muted/40">
            <tr>
              <Th className={compact ? 'px-2.5' : undefined}>{table.label_header}</Th>
              {table.columns.map((c) => <Th key={c.field} align="right" className={compact ? 'px-2.5' : undefined}>{c.label}</Th>)}
            </tr>
          </THead>
          <tbody>
            {table.rows.map((r, i) => (
              <tr key={`${r.label}-${i}`} className="border-t border-border/70">
                <td className={cn('max-w-[16rem]', pad)}>
                  <span className="flex items-center gap-2">
                    <span className="w-4 shrink-0 text-right text-[13px] tabular-nums text-muted-foreground">{i + 1}</span>
                    <span className={cn('font-medium', compact ? 'leading-snug' : 'truncate')} title={r.label}>{r.label}</span>
                  </span>
                </td>
                {r.cells.map((c) => (
                  <td key={c.field} className={cn('whitespace-nowrap text-right tabular-nums', pad)}>
                    {!compact && c.field === table.bar_field && table.bar_max > 0 && typeof c.value === 'number'
                      ? <span className="inline-block text-right"><BarCell value={c.value} max={table.bar_max}>{c.display}</BarCell></span>
                      : c.display}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.rows_total != null && table.rows_total > shown && (
        <p className="border-t border-border/70 px-4 py-2 text-[13px] text-muted-foreground">The top {shown} of {table.rows_total}. The report has them all.</p>
      )}
    </div>
  );
}

function ChartTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-[13px] shadow-md">
      <div className="font-semibold text-foreground">{dayShort(p.x)}</div>
      <div className="tabular-nums text-foreground">{p.display}</div>
    </div>
  );
}

function AnswerChart({ chart }) {
  const T = useChartTheme();
  const gid = `ask-area-${React.useId().replace(/:/g, "")}`;
  if (!chart?.points?.length) return null;
  // Axis ticks reuse the shape of the point labels (the server formatted
  // every point); the axis only needs a compact scale, not the figures.
  const compact = (v) => {
    if (chart.format === 'percent') return `${v}%`;
    if (chart.format === 'ratio') return `${v}x`;
    return new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
  };
  return (
    <div className="rounded-xl border border-[hsl(var(--card-border))] bg-card p-3">
      <p className="mb-2 px-1 text-[13px] font-medium text-muted-foreground">{chart.label}, day by day</p>
      <GChartCanvas>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chart.points} margin={{ top: 10, right: 8, left: -6, bottom: 0 }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={T.blue} stopOpacity={T.areaTop} />
                <stop offset="1" stopColor={T.blue} stopOpacity="0" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={T.grid} vertical={false} />
            <XAxis dataKey="x" tickFormatter={dayShort} tick={{ fontSize: 11.5, fill: T.lbl }} minTickGap={24} axisLine={false} tickLine={false} />
            <YAxis width={48} tickFormatter={compact} tick={{ fontSize: 11.5, fill: T.lbl }} axisLine={false} tickLine={false} />
            <ReTooltip content={<ChartTip />} cursor={{ stroke: T.crosshair, strokeWidth: 1 }} />
            <Area dataKey="y" type="monotone" stroke={T.blue} strokeWidth={2.2} fill={`url(#${gid})`} dot={false} connectNulls={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </GChartCanvas>
    </div>
  );
}

/** One answer, laid out: the sentence, the numbers, the shape, then how it was counted. */
export function AnswerPanel({ r, onFollowUp, onNavigate, compact = false }) {
  const p = r.panel || {};
  const rejected = r.status === 'rejected';
  const failed = rejected || r.status === 'declined' || r.status === 'error';
  return (
    <div className="space-y-4">
      {failed ? (
        <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[14px] leading-relaxed text-amber-900 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>{p.summary?.text || r.answer}</p>
        </div>
      ) : (
        <p className={cn('copy-col font-medium leading-snug tracking-[-0.01em] text-foreground', compact ? 'text-[15px]' : 'text-[17px]')}>{p.summary?.text || r.answer}</p>
      )}

      {/* An explanation from the guides (Qbee): short paragraphs or steps. */}
      {p.explain?.length > 0 && (
        <div className="space-y-2 text-[14px] leading-relaxed text-foreground/90">
          {p.explain.map((e, i) => <p key={i}>{e.text}</p>)}
        </div>
      )}
      {p.sources?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {p.sources.map((s) => (
            <a key={s.url} href={s.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--card-border))] bg-card px-2.5 py-1.5 text-[13px] font-medium hover:border-foreground/40">
              <BookOpen className="size-3.5 text-muted-foreground" /> {s.title} <ArrowUpRight className="size-3.5" />
            </a>
          ))}
        </div>
      )}

      {p.window?.label && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
          <CalendarRange className="size-3.5" />
          <span className="font-medium text-foreground">{p.window.label}</span>
          {p.window.days ? <span>{p.window.days} day{p.window.days === 1 ? '' : 's'}{p.window.timezone ? `, ${p.window.timezone} time` : ''}</span> : null}
        </p>
      )}

      <AnswerTiles tiles={p.tiles} compact={compact} />
      <AnswerChart chart={p.chart} />
      <AnswerTable table={p.table} compact={compact} />

      {(p.notes?.length > 0 || p.window?.note) && (
        <ul className="space-y-1 text-[14px] leading-relaxed text-foreground/90">
          {p.window?.note && !p.notes?.some((n) => n.text === p.window.note) && <li>{p.window.note}</li>}
          {p.notes?.map((n, i) => <li key={i}>{n.text}</li>)}
        </ul>
      )}

      {p.definitions?.length > 0 && (
        <div className="rounded-lg bg-muted/40 px-3 py-2.5">
          <p className="mb-1 flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground"><BookOpen className="size-3.5" /> How these are counted</p>
          <ul className="space-y-0.5 text-[13px] leading-relaxed text-muted-foreground">
            {p.definitions.map((d) => <li key={d}>{d}</li>)}
          </ul>
        </div>
      )}

      {p.reports?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {p.reports.map((rep) => (
            <Link key={rep.to} to={rep.to} onClick={onNavigate}
              className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--card-border))] bg-card px-2.5 py-1.5 text-[13px] font-medium hover:border-foreground/40">
              See it in {rep.label} <ArrowUpRight className="size-3.5" />
            </Link>
          ))}
        </div>
      )}

      {p.follow_ups?.length > 0 && (
        <div className="space-y-1.5 border-t border-border/70 pt-3">
          <p className="text-[13px] font-medium text-muted-foreground">Ask next</p>
          <div className="flex flex-wrap gap-2">
            {p.follow_ups.map((q) => (
              <button key={q} type="button" onClick={() => onFollowUp(q)}
                className="rounded-full border border-border bg-surface-subtle px-3 py-1.5 text-left text-[13px] text-foreground transition-colors hover:border-primary/60 hover:bg-primary/5">
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {r.tool_calls?.length > 0 && (
        <p className="text-[13px] text-muted-foreground">
          Read from: {[...new Set(r.tool_calls.map((t) => `${TOOL_WORDS[t.name] || t.name}${t.error ? ' (could not be read)' : ''}`))].join(', ')}.
        </p>
      )}
    </div>
  );
}

function AnswerSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <p className="flex items-center gap-2 text-[14px] text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Reading your reports…</p>
      <Skeleton className="h-6 w-4/5" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Skeleton className="h-[104px]" /><Skeleton className="h-[104px]" />
      </div>
      <Skeleton className="h-40" />
    </div>
  );
}

/* ── the panel ───────────────────────────────────────────────────────────── */

export function AskPanel({ open, onOpenChange, initialQuestion = '' }) {
  const qc = useQueryClient();
  const [q, setQ] = useState(initialQuestion);
  // What is on screen: a fresh answer, or one reopened from history.
  const [shown, setShown] = useState(null);
  const inputRef = useRef(null);
  const { data: status } = useQuery({
    queryKey: ['ask-status'],
    queryFn: async () => (await api.get('/api/ask/status')).data,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const { data: history = [] } = useQuery({
    queryKey: ['ask-history'],
    queryFn: async () => (await api.get('/api/ask/history')).data,
    enabled: open,
    staleTime: 30_000,
  });
  const ask = useMutation({
    mutationFn: async (question) => (await api.post('/api/ask', { question }, { timeout: 150_000 })).data,
    onSuccess: (data, question) => {
      setShown({ ...data, question });
      qc.invalidateQueries({ queryKey: ['ask-history'] });
    },
  });
  const run = (text) => {
    const t = String(text || '').trim();
    if (t.length < 3 || ask.isPending) return;
    setQ(t);
    setShown(null);
    ask.mutate(t);
  };

  useEffect(() => {
    if (!open) return;
    setQ(initialQuestion);
    if (initialQuestion && initialQuestion.trim().length >= 3) run(initialQuestion);
    else { ask.reset(); setShown(null); }
    setTimeout(() => inputRef.current?.focus(), 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuestion]);

  const submit = (e) => { e?.preventDefault(); run(q); };
  const r = shown;
  const notConfigured = (r && r.configured === false) || (status && status.configured === false);
  const err = ask.error ? (ask.error.response?.data?.error || ask.error.message) : null;
  const past = history.filter((h) => h.status !== 'not_configured');
  const openPast = (h) => {
    setQ(h.question);
    ask.reset();
    setShown({
      question: h.question, status: h.status, answer: h.answer, reopened: h.created_at,
      panel: h.answer_spec || { summary: { text: h.answer }, reports: h.reports || [] },
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
        <SheetHeader className="space-y-1 border-b border-border px-5 pb-4 pt-5 text-left">
          <SheetTitle className="flex items-center gap-2 text-[17px]"><Sparkles className="size-4 text-primary" /> Qbee</SheetTitle>
          <SheetDescription className="text-[13px]">Answers come from your own reports, with the dates and the report that shows each number.</SheetDescription>
          <form onSubmit={submit} className="flex items-center gap-2 pt-2">
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} maxLength={500} aria-label="Your question"
              placeholder="Which campaign brought repeat buyers last month?" disabled={notConfigured}
              className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60" />
            <Button type="submit" disabled={ask.isPending || q.trim().length < 3 || notConfigured}>
              {ask.isPending ? <Loader2 className="size-4 animate-spin" /> : <>Ask <CornerDownLeft className="ml-1 size-3.5 opacity-70" /></>}
            </Button>
          </form>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* Past questions LEFT, the answer RIGHT: the list chooses, the story reads. */}
          <aside className="order-2 border-t border-border md:order-1 md:w-60 md:shrink-0 md:overflow-y-auto md:border-r md:border-t-0">
            <p className="flex items-center gap-1.5 px-4 pb-1 pt-4 text-[13px] font-medium text-muted-foreground"><History className="size-3.5" /> Your questions</p>
            {past.length === 0 ? (
              <p className="px-4 pb-4 text-[13px] leading-relaxed text-muted-foreground">What you ask is kept here, so you can reopen an answer.</p>
            ) : (
              <ul className="px-2 pb-3">
                {past.map((h) => (
                  <li key={h.id}>
                    <button type="button" onClick={() => openPast(h)}
                      className={cn('w-full rounded-md px-2 py-2 text-left transition-colors hover:bg-muted',
                        r && r.question === h.question && (r.id === h.id || r.reopened === h.created_at) && 'bg-muted')}>
                      <span className="line-clamp-2 text-[13.5px] leading-snug text-foreground">{h.question}</span>
                      <span className="mt-0.5 block text-[12px] text-muted-foreground">{whenAsked(h.created_at)}{h.status === 'rejected' ? ', not shown' : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <section className="order-1 min-w-0 flex-1 overflow-y-auto px-5 py-5 md:order-2">
            {notConfigured && (
              <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[14px] leading-relaxed text-amber-900 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <p>{r?.needs || status?.needs || 'Qbee needs an AI provider key. A platform admin adds it in the console.'}</p>
              </div>
            )}
            {!notConfigured && ask.isPending && <AnswerSkeleton />}
            {!notConfigured && err && !ask.isPending && <p className="text-[14px] text-rose-700 dark:text-rose-400">{err}</p>}
            {!notConfigured && r && r.configured !== false && !ask.isPending && (
              <>
                {r.reopened && (
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-[13px] text-muted-foreground">
                    <span>Answered {whenAsked(r.reopened)}. The numbers are as they were then.</span>
                    <button type="button" className="font-medium text-primary hover:underline" onClick={() => run(r.question)}>Ask again now</button>
                  </div>
                )}
                <AnswerPanel r={r} onFollowUp={run} onNavigate={() => onOpenChange(false)} />
              </>
            )}
            {!notConfigured && !r && !ask.isPending && !err && (
              <div className="space-y-2">
                <p className="text-[13px] font-medium text-muted-foreground">Try one of these</p>
                <div className="flex flex-col items-start gap-2">
                  {EXAMPLES.map((x) => (
                    <button key={x} type="button" onClick={() => run(x)}
                      className="rounded-full border border-border bg-surface-subtle px-3 py-1.5 text-left text-[13.5px] hover:border-primary/60 hover:bg-primary/5">{x}</button>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * The bar itself: type a question and press Enter. The search (Cmd+K) keeps
 * its own button at the end of the box, so nothing that was in the top bar
 * went away.
 */
// The question bar at the top of every page: it asks Qbee, whose answer
// opens in the dock beside the page (components/qbee). Without the dock (a
// page outside the app shell) it falls back to the old panel.
export function AskBar({ onOpenSearch }) {
  const qbee = useQbee();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [asked, setAsked] = useState('');
  const go = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (qbee) {
      if (t.length >= 3) { qbee.ask(t); setText(''); } else qbee.openDock();
      return;
    }
    setAsked(t);
    setOpen(true);
  };
  return (
    <>
      <form onSubmit={go}
        className={cn(
          // With the dock open it has its own box, and the page beside it is
          // narrower than the window says: the top bar keeps only the icon.
          'hidden h-9 w-full max-w-[30rem] justify-self-center items-center gap-2 rounded-md border border-border bg-surface-subtle pl-3 pr-1 text-[14px]',
          !qbee?.open && 'xl:flex',
          'focus-within:border-border-strong focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1'
        )}>
        <Sparkles className="size-4 shrink-0 text-muted-foreground" />
        <input value={text} onChange={(e) => setText(e.target.value)} aria-label="Ask Qbee about your numbers or this page"
          placeholder="Ask Qbee…" className="h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground" />
        <button type="button" onClick={onOpenSearch} title="Search leads, jump to a page (⌘K)"
          className="inline-flex h-7 items-center gap-1 rounded px-1.5 text-[12px] text-muted-foreground hover:bg-accent hover:text-foreground">
          <Search className="size-3.5" /> <kbd className="font-mono">⌘K</kbd>
        </button>
      </form>
      <Button variant="ghost" size="icon-sm" className={cn(!qbee?.open && 'xl:hidden', qbee?.open && 'bg-muted text-foreground')} aria-label="Ask Qbee" title="Ask Qbee"
        onClick={() => { if (qbee) { qbee.toggle(); return; } setAsked(''); setOpen(true); }}>
        <Sparkles className="size-4" />
      </Button>
      {!qbee && <AskPanel open={open} onOpenChange={setOpen} initialQuestion={asked} />}
    </>
  );
}
